/* =========================================================================
   Penny Premium 3D — hero-orbit.js
   Upgrades the server-rendered hero fan into a rotating product wheel
   (the reference video's "spinning cans" stage).

   The ring is simulated with flat 2D transforms — per frame each card gets
   translate/scale/z-index/opacity computed from its angle on the circle
   (x = sin·r, projected scale = p/(p − cos·r)). No preserve-3d, no CSS
   perspective: iOS Safari's 3D compositing bugs (giant smeared cards) go
   away, backs of cards are never mirrored, and every frame is a cheap
   composited transform. Interactions: drag / touch-swipe, wheel-over-ring,
   scroll-linked nudge, slow idle spin, hover pause + detail pill.
   Without JS the static fan from the section CSS remains.
   ========================================================================= */

function initOrbit(root) {
  if (root.dataset.orbitReady) return;
  root.dataset.orbitReady = 'true';

  const wheel = root.querySelector('[data-orbit-wheel]');
  const items = wheel ? Array.from(wheel.querySelectorAll('.hero-orbit__item')) : [];
  const n = items.length;
  if (!wheel || n < 3) return;

  root.classList.add('hero-orbit--wheel');

  const step = 360 / n;
  let radius = 300;

  const setRadius = () => {
    const w = items[0].getBoundingClientRect().width || 200;
    const phone = window.matchMedia('(max-width: 749px)').matches;
    const spread = phone ? 1.05 : 1.25;
    const floor = phone ? w * 1.02 : w * 1.35;
    radius = Math.max((w * n) / (2 * Math.PI) * spread, floor);
    if (phone) radius = Math.min(radius, window.innerWidth * 0.62);
  };
  setRadius();
  window.addEventListener('resize', setRadius);

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let angle = 0;
  let target = 0;
  let dragging = false;
  let hovering = false;
  let lastX = 0;
  let moved = 0;

  /* --- Hover: pause the wheel, show the product's detail pill ----------- */
  const detail = root.querySelector('[data-orbit-detail]');
  const detailTitle = detail?.querySelector('[data-detail-title]');
  const detailPrice = detail?.querySelector('[data-detail-price]');
  const detailCompare = detail?.querySelector('[data-detail-compare]');
  const detailDesc = detail?.querySelector('[data-detail-desc]');
  let hideTimer;

  const hideDetail = () => {
    hovering = false;
    root.classList.remove('hero-orbit--detail-open');
    if (!detail) return;
    detail.classList.remove('is-visible');
    hideTimer = setTimeout(() => detail.setAttribute('hidden', ''), 250);
  };

  if (detail) {
    items.forEach((item) => {
      item.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'mouse' || !item.dataset.title) return;
        hovering = true;
        clearTimeout(hideTimer);
        detailTitle.textContent = item.dataset.title;
        detailPrice.textContent = item.dataset.price || '';
        if (item.dataset.compare) {
          detailCompare.textContent = item.dataset.compare;
          detailCompare.removeAttribute('hidden');
        } else {
          detailCompare.setAttribute('hidden', '');
        }
        detailDesc.textContent = item.dataset.desc || '';
        detail.removeAttribute('hidden');
        root.classList.add('hero-orbit--detail-open');
        requestAnimationFrame(() => detail.classList.add('is-visible'));
      });
      item.addEventListener('pointerleave', (event) => {
        if (event.pointerType !== 'mouse') return;
        hideDetail();
      });
    });
  }

  /* --- Interactions ------------------------------------------------------ */
  root.addEventListener('pointerdown', (event) => {
    dragging = true;
    moved = 0;
    lastX = event.clientX;
    root.classList.add('is-dragging');
  });

  window.addEventListener('pointermove', (event) => {
    if (!dragging) return;
    const dx = event.clientX - lastX;
    moved += Math.abs(dx);
    target += dx * 0.28;
    lastX = event.clientX;
  });

  const stopDrag = () => {
    dragging = false;
    root.classList.remove('is-dragging');
  };
  window.addEventListener('pointerup', stopDrag);
  window.addEventListener('pointercancel', stopDrag);

  // A drag must not fire the card's product link
  root.addEventListener(
    'click',
    (event) => {
      if (moved > 8) {
        event.preventDefault();
        event.stopPropagation();
      }
    },
    true
  );

  // Wheel over the carousel spins it instead of scrolling the page
  wheel.addEventListener(
    'wheel',
    (event) => {
      event.preventDefault();
      target += (event.deltaY + event.deltaX) * 0.22;
    },
    { passive: false }
  );

  // Page scroll nudges the ring — the video's scroll-linked spin.
  // Scrolling also ends a hover pause so the wheel picks back up.
  let lastScroll = window.scrollY;
  window.addEventListener(
    'scroll',
    () => {
      target += (window.scrollY - lastScroll) * 0.1;
      lastScroll = window.scrollY;
      if (hovering) hideDetail();
    },
    { passive: true }
  );

  /* --- Render loop: simulated ring, flat transforms ---------------------- */
  const DEG = Math.PI / 180;

  const render = () => {
    const p = radius * 4; // virtual camera distance → front card ~1.33x
    for (let i = 0; i < n; i++) {
      const th = (i * step + angle) * DEG;
      const cos = Math.cos(th);
      const sin = Math.sin(th);
      const scale = p / (p - cos * radius);
      const x = sin * radius * scale;
      const item = items[i];
      // perspective() + rotateY on the element itself is single-element 3D —
      // safe everywhere (only nested preserve-3d breaks iOS Safari)
      item.style.transform =
        'translate(calc(-50% + ' + x.toFixed(1) + 'px), -50%) scale(' + scale.toFixed(3) + ')' +
        ' perspective(800px) rotateY(' + (-sin * 24).toFixed(1) + 'deg)';
      item.style.zIndex = String(Math.round(cos * 100) + 100);
      item.style.opacity = (0.55 + 0.45 * (cos + 1) / 2).toFixed(3);
    }
  };

  const loop = () => {
    if (!reduced && !dragging && !hovering) target += 0.05; // slow idle spin
    angle += (target - angle) * 0.08;
    render();
    requestAnimationFrame(loop);
  };
  requestAnimationFrame(loop);
}

function initAll() {
  document.querySelectorAll('[data-hero-orbit]').forEach(initOrbit);
}

initAll();
document.addEventListener('shopify:section:load', initAll);
